import { createFileRoute } from '@tanstack/react-router'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { User, Trash2, Loader2, AlertCircle, Bell, Download } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { useSubscription } from '@/hooks/use-subscription'
import { Link } from '@tanstack/react-router'

export const Route = createFileRoute('/dashboard/settings/')({ 
  component: SettingsPage
})

function SettingsPage() {
  const [emailThreshold, setEmailThreshold] = useState(90)
  const { user, loading: authLoading } = useAuth()
  const { subscription, loading: subscriptionLoading } = useSubscription()
  
  const loading = authLoading || subscriptionLoading

  const handleUpdateThreshold = async () => {
    // TODO: Implement API call to update email threshold
    console.log('Updating email threshold to:', emailThreshold)
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading settings...</span>
          </div>
        </div>
      </ProtectedRoute>
    )
  }

  if (!user) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to load user data</h3>
              <p className="text-gray-500">Please try refreshing the page</p>
            </div>
          </div>
        </div>
      </ProtectedRoute>
    )
  }

  const currentPlan = subscription?.plan ? {
    name: subscription.plan.name,
    credits: 0, // TODO: Get actual usage from usage API
    totalCredits: subscription.plan.credits_included || subscription.plan.credits || 1000
  } : {
    name: 'Free',
    credits: 0,
    totalCredits: 1000
  }

  return (
    <ProtectedRoute>
      <div className="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="dashboard-page-title">Settings</h1>
          <p className="dashboard-page-description">Manage your account settings and preferences</p>
        </div>

        {/* Account Information */}
        <Card className="overflow-hidden bg-white/80 shadow-sm">
          <CardHeader className="border-b border-stone-100">
            <CardTitle>Account Information</CardTitle>
            <CardDescription>
              Update your account details and preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-stone-100 ring-1 ring-stone-200">
                {user.user_metadata?.avatar_url ? (
                  <img 
                    src={user.user_metadata.avatar_url} 
                    alt="Profile" 
                    className="h-16 w-16 rounded-full"
                  />
                ) : (
                  <User className="w-8 h-8 text-stone-500" />
                )}
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-medium text-stone-950">
                  {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                </h3>
                <p className="text-stone-500">{user.email}</p>
                <Badge variant="secondary" className="bg-stone-100 text-stone-700">{currentPlan.name}</Badge>
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="account-name">Account name</Label>
                <Input 
                  id="account-name" 
                  value={user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'} 
                  readOnly 
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="associated-email">Associated email</Label>
              <Input id="associated-email" value={user.email || ''} readOnly />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-id">User ID</Label>
              <Input id="user-id" value={user.id} readOnly className="font-mono text-sm" />
            </div>
          </CardContent>
        </Card>

        {/* Email Usage Alerts */}
        <Card className="overflow-hidden bg-white/80 shadow-sm">
          <CardHeader className="border-b border-stone-100">
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-stone-500" />
              Email usage alerts
            </CardTitle>
            <CardDescription>
              An alert will be sent to your email when your monthly usage reaches the set threshold.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-4 rounded-2xl bg-stone-100/70 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-2 flex-1">
                <Label htmlFor="alert-threshold">Your alert threshold is currently set to:</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="alert-threshold"
                    type="number"
                    value={emailThreshold}
                    onChange={(e) => setEmailThreshold(Number(e.target.value))}
                    className="w-20"
                    min="0"
                    max="100"
                  />
                  <span className="text-sm text-stone-500">%</span>
                  <Button variant="outline" size="sm" onClick={handleUpdateThreshold}>
                    Update
                  </Button>
                </div>
              </div>
              <div>
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">Enabled</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Actions */}
        <Card className="overflow-hidden bg-white/80 shadow-sm">
          <CardHeader className="border-b border-stone-100">
            <CardTitle>Account Actions</CardTitle>
            <CardDescription>
              Manage your account and data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-4 rounded-2xl border border-stone-200/80 bg-[#fffdfa] p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="font-medium text-stone-950">Export Data</h4>
                <p className="text-sm text-stone-500">Download all your account data</p>
              </div>
              <Button variant="outline" disabled>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
            
            <div className="flex flex-col gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="font-medium text-red-900">Delete Account</h4>
                <p className="text-sm text-red-600">Permanently delete your account and all data</p>
              </div>
              <Button variant="destructive" disabled>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </ProtectedRoute>
  )
}
